/**
 * Auth client backed by Lovable Cloud (Supabase).
 *
 * Exposes a small better-auth-style surface (signIn / signUp / signOut /
 * authClient + useSession + ProtectedRoute) so the existing auth pages keep
 * working while authentication runs on Lovable Cloud.
 */
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import MfaGate, { resetMfaGate } from "@/components/auth/MfaGate";
import {
  requestPasswordReset,
  verifySignIn,
} from "@/lib/auth/rate-limit.functions";
import {
  enableBiometricLogin,
  getBiometricCredential,
  disableBiometricLogin,
  BiometricError,
} from "@/lib/native/biometric";

/**
 * Silently stash the current session's refresh token in the device secure
 * store so the user can sign in with their fingerprint next time. No-op on web
 * and on devices without enrolled biometrics.
 */
async function rememberBiometricSession(
  email: string | undefined,
  refreshToken: string | undefined | null,
): Promise<void> {
  if (!email || !refreshToken) return;
  try {
    await enableBiometricLogin(email, refreshToken);
  } catch {
    /* best effort — never block sign-in. */
  }
}

export interface SessionUser {
  id: string;
  name?: string;
  email?: string;
  image?: string;
  isAdmin?: boolean;
  emailVerified?: boolean;
  companyName?: string | null;
}

type AuthErrorReason = import("@/lib/native/biometric").BiometricErrorReason;
type Result<T = unknown> = {
  data: T | null;
  error: { message: string; reason?: AuthErrorReason } | null;
};

function ok<T>(data: T): Result<T> {
  return { data, error: null };
}
function fail(message: string, reason?: AuthErrorReason): Result<never> {
  return { data: null, error: { message, reason } };
}

async function fetchIsAdmin(userId: string): Promise<boolean> {
  const { data } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  return data === true;
}

/* ------------------------------------------------------------------ */
/* Brute-force rate limiting (DB-backed)                               */
/* ------------------------------------------------------------------ */

function formatRetry(seconds: number): string {
  if (seconds <= 90) return `${Math.max(1, Math.ceil(seconds))} seconds`;
  return `${Math.ceil(seconds / 60)} minutes`;
}

/* Rate limiting now happens server-side, inline with credential verification
 * (see `verifySignIn` / `requestPasswordReset` in rate-limit.functions.ts), so
 * there is no public attempt-recording endpoint for an attacker to abuse. */


/* ------------------------------------------------------------------ */
/* signIn / signUp / signOut                                           */
/* ------------------------------------------------------------------ */

export const signIn = {
  async email({ email, password }: { email: string; password: string }) {
    const id = email.trim().toLowerCase();

    // Credentials are verified on the server, where the rate-limit attempt is
    // recorded only for genuinely observed failures.
    let result: Awaited<ReturnType<typeof verifySignIn>>;
    try {
      result = await verifySignIn({ data: { email: id, password } });
    } catch {
      return fail("Unable to sign in right now. Please try again.");
    }

    if (!result.ok) {
      if (result.locked) {
        return fail(
          `Too many sign-in attempts. Please try again in ${formatRetry(result.retryAfterSeconds ?? 0)}.`,
        );
      }
      return fail(result.message);
    }

    // Adopt the server-verified session in the browser client.
    const { data, error } = await supabase.auth.setSession({
      access_token: result.accessToken,
      refresh_token: result.refreshToken,
    });
    if (error || !data.session) {
      return fail(error?.message ?? "Unable to establish session.");
    }
    // Stash the refresh token for fingerprint sign-in (native only, best effort).
    await rememberBiometricSession(data.user?.email ?? id, data.session.refresh_token);
    const isAdmin = data.user ? await fetchIsAdmin(data.user.id) : false;
    return ok({ user: { ...data.user, isAdmin } });
  },

  /**
   * Fingerprint / Face sign-in (native app only). Prompts the OS biometric
   * dialog, then exchanges the securely-stored refresh token for a fresh
   * session — no password required.
   */
  async biometric() {
    try {
      const { refreshToken } = await getBiometricCredential();
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: refreshToken,
      });
      if (error || !data.session || !data.user) {
        // Stored token is stale/revoked — clear it so we stop offering it.
        await disableBiometricLogin();
        return fail(
          "Fingerprint sign-in is no longer valid. Please sign in with your password.",
          "no-credential",
        );
      }
      // Token rotated on refresh — re-store the new one for next time.
      await rememberBiometricSession(data.user.email, data.session.refresh_token);
      const isAdmin = await fetchIsAdmin(data.user.id);
      return ok({ user: { ...data.user, isAdmin } });
    } catch (e) {
      if (e instanceof BiometricError) {
        // Sensor gone / permission revoked / no stored credential → stop
        // offering biometric so the UI cleanly falls back to email login.
        if (e.reason === "unavailable" || e.reason === "no-credential") {
          await disableBiometricLogin();
        }
        return fail(e.message, e.reason);
      }
      const message =
        e instanceof Error
          ? e.message
          : "Fingerprint sign-in failed. Please use your password.";
      return fail(message, "failed");
    }
  },
  async social({
    provider,
    callbackURL,
  }: {
    provider: "google" | "facebook" | "apple";
    callbackURL?: string;
    newUserCallbackURL?: string;
    errorCallbackURL?: string;
  }) {
    if (provider !== "google" && provider !== "apple") {
      return fail(`${provider} sign-in is not available yet.`);
    }
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: callbackURL ?? window.location.origin,
    });
    if (result.error) {
      const message =
        result.error instanceof Error ? result.error.message : String(result.error);
      return fail(message);
    }
    return ok(result);
  },
};

export const signUp = {
  async email({
    name,
    email,
    password,
    companyName,
  }: {
    name: string;
    email: string;
    password: string;
    companyName?: string;
  }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name, companyName: companyName ?? "" },
        emailRedirectTo: `${window.location.origin}/verify-email`,
      },
    });
    if (error) return fail(error.message);
    return ok(data);
  },
};

export async function signOut() {
  // Explicit sign-out revokes the stored refresh token, so clear biometric too
  // (native only — no-op on web). User re-enables it on their next password login.
  await disableBiometricLogin();
  const { error } = await supabase.auth.signOut();
  if (error) return fail(error.message);
  return ok(null);
}

/* ------------------------------------------------------------------ */
/* authClient — password reset, email verification, profile           */
/* ------------------------------------------------------------------ */

export const authClient = {
  async forgetPassword({
    email,
    redirectTo,
  }: {
    email: string;
    redirectTo?: string;
  }) {
    const id = email.trim().toLowerCase();
    const redirect = redirectTo ?? `${window.location.origin}/reset-password`;

    // The reset email is dispatched server-side, where the rate-limit attempt
    // is recorded — no public endpoint to forge reset requests against.
    let result: Awaited<ReturnType<typeof requestPasswordReset>>;
    try {
      result = await requestPasswordReset({ data: { email: id, redirectTo: redirect } });
    } catch {
      // Fail open on limiter/transport errors — never block a real user.
      return ok(null);
    }
    if (result.locked) {
      return fail(
        `Too many password reset requests. Please try again in ${formatRetry(result.retryAfterSeconds)}.`,
      );
    }
    return ok(null);
  },
  async resetPassword({ newPassword }: { newPassword: string; token?: string }) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return fail(error.message);
    return ok(null);
  },
  /**
   * Start an email-address change for the signed-in user. Supabase sends a
   * confirmation link to the NEW address (and, when "Secure email change" is
   * enabled, also to the current address). The change only takes effect after
   * the link is clicked, which lands back on `/verify-email`.
   */
  async updateEmail({ newEmail }: { newEmail: string }) {
    const { error } = await supabase.auth.updateUser(
      { email: newEmail.trim().toLowerCase() },
      { emailRedirectTo: `${window.location.origin}/verify-email` },
    );
    if (error) return fail(error.message);
    return ok(null);
  },
  async sendVerificationEmail({ email }: { email: string; callbackURL?: string }) {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/verify-email` },
    });
    if (error) return fail(error.message);
    return ok(null);
  },
  async verifyEmail(): Promise<Result<{ signedIn: boolean }>> {
    // Supabase confirms the email SERVER-SIDE at its /auth/v1/verify endpoint
    // BEFORE redirecting back here, then appends either a session (implicit
    // flow) or a `code` (PKCE flow) to this URL. The client parses that URL
    // (detectSessionInUrl) ASYNCHRONOUSLY, so getUser() can momentarily return
    // no user right after mount even though confirmation succeeded.
    //
    // Three outcomes:
    //  1. URL carries an explicit error (e.g. expired/used link)  -> failure.
    //  2. A session gets established (confirmed + signed in here)  -> success.
    //  3. No error, but no session (e.g. the link was opened on a different
    //     device/browser than sign-up, so the PKCE verifier is missing) ->
    //     the email IS already confirmed; we just can't sign them in here.
    //     Treat as success and ask them to sign in.
    const params = new URLSearchParams(
      typeof window !== "undefined"
        ? `${window.location.search.slice(1)}&${window.location.hash.slice(1)}`
        : "",
    );
    const urlError = params.get("error_description") || params.get("error");
    const errorCode = params.get("error_code");
    if (urlError) {
      const expired = errorCode === "otp_expired" || /expired/i.test(urlError);
      return fail(
        expired
          ? "This verification link has expired or already been used. Please sign in, or request a new link."
          : decodeURIComponent(urlError),
      );
    }

    const isConfirmed = async () => {
      const { data } = await supabase.auth.getUser();
      return Boolean(data.user?.email_confirmed_at);
    };

    if (await isConfirmed()) return ok({ signedIn: true });

    // Wait (up to ~6s) for the auth client to finish processing the URL,
    // listening for auth-state changes and polling as a fallback.
    const settled = await new Promise<boolean>((resolve) => {
      let done = false;
      let poll: ReturnType<typeof setInterval>;
      let timeout: ReturnType<typeof setTimeout>;
      let sub: { subscription: { unsubscribe: () => void } } | undefined;

      const finish = (value: boolean) => {
        if (done) return;
        done = true;
        clearInterval(poll);
        clearTimeout(timeout);
        sub?.subscription.unsubscribe();
        resolve(value);
      };

      sub = supabase.auth.onAuthStateChange((_event, session) => {
        if (session?.user?.email_confirmed_at) finish(true);
      }).data;

      poll = setInterval(async () => {
        if (await isConfirmed()) finish(true);
      }, 500);

      timeout = setTimeout(() => finish(false), 6000);
    });

    if (settled || (await isConfirmed())) return ok({ signedIn: true });

    // No error in the URL and no session: the server already confirmed the
    // email, but this device can't complete sign-in. Don't scare the user with
    // a "failed" message — their account is verified.
    return ok({ signedIn: false });
  },
  async updateProfile({
    name,
    companyName,
  }: {
    name?: string;
    companyName?: string;
  }) {
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData.user?.id;
    if (!uid) return fail("You're not signed in.");
    const update: { name?: string; company_name?: string } = {};
    if (name !== undefined) update.name = name;
    if (companyName !== undefined) update.company_name = companyName;
    const { error } = await supabase
      .from("profiles")
      .update(update)
      .eq("id", uid);
    if (error) return fail(error.message);
    return ok(null);
  },
};

/* ------------------------------------------------------------------ */
/* useSession                                                          */
/* ------------------------------------------------------------------ */

interface SessionState {
  user: SessionUser | null;
  isPending: boolean;
  profileLoaded: boolean;
}

type SessionResult = {
  session: { user: SessionUser } | null;
  user: SessionUser | null;
  isPending: boolean;
  isProfileLoaded: boolean;
  error: null;
  isAuthenticated: boolean;
};

const SessionContext = createContext<SessionResult | null>(null);

const unauthenticatedSession: SessionResult = {
  session: null,
  user: null,
  isPending: true,
  isProfileLoaded: false,
  error: null,
  isAuthenticated: false,
};

function useSessionState(): SessionResult {
  const [state, setState] = useState<SessionState>({
    user: null,
    isPending: true,
    profileLoaded: false,
  });

  useEffect(() => {
    let active = true;
    // Tracks the user currently reflected in state so we can ignore the
    // session re-emits Supabase fires whenever the tab regains focus
    // (TOKEN_REFRESHED, and a repeat SIGNED_IN for the same user). Without
    // this guard those events reset state back to `profileLoaded: false`,
    // flashing the whole app through its loading paths on every tab switch —
    // which looks like a full page refresh on desktop.
    let currentUserId: string | null = null;

    function applyBase(sessionUser: {
      id: string;
      email?: string;
      email_confirmed_at?: string;
      user_metadata?: Record<string, unknown>;
    } | null) {
      if (!sessionUser) {
        currentUserId = null;
        if (active) setState({ user: null, isPending: false, profileLoaded: true });
        return;
      }
      currentUserId = sessionUser.id;
      const meta = sessionUser.user_metadata ?? {};
      const base: SessionUser = {
        id: sessionUser.id,
        email: sessionUser.email,
        name: (meta.name as string) || (meta.full_name as string) || "",
        image: (meta.avatar_url as string) || undefined,
        emailVerified: !!sessionUser.email_confirmed_at,
      };
      if (active) setState({ user: base, isPending: false, profileLoaded: false });
      // Enrich with profile + admin role (deferred to avoid auth-callback deadlock).
      setTimeout(async () => {
        if (!active) return;
        const [{ data: profile }, isAdmin] = await Promise.all([
          supabase.from("profiles").select("*").eq("id", sessionUser.id).maybeSingle(),
          fetchIsAdmin(sessionUser.id),
        ]);
        if (!active) return;
        setState({
          user: {
            ...base,
            name: profile?.name || base.name,
            image: profile?.image || base.image,
            companyName: profile?.company_name ?? null,
            isAdmin,
          },
          isPending: false,
          profileLoaded: true,
        });
      }, 0);
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id ?? null;
      // Ignore the session re-emits that fire on tab focus / token refresh for
      // the user we're already showing. Re-running applyBase here would bounce
      // the app back to its loading state and flash like a refresh.
      if (
        (event === "TOKEN_REFRESHED" || event === "SIGNED_IN" || event === "USER_UPDATED") &&
        nextUserId &&
        nextUserId === currentUserId
      ) {
        return;
      }
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") resetMfaGate();
      applyBase(session?.user ?? null);
    });

    supabase.auth.getSession().then(({ data }) => {
      applyBase(data.session?.user ?? null);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  return {
    session: state.user ? { user: state.user } : null,
    user: state.user,
    isPending: state.isPending,
    isProfileLoaded: state.profileLoaded,
    error: null,
    isAuthenticated: !state.isPending && !!state.user,
  };
}

export function useSession() {
  const sharedSession = useContext(SessionContext);
  return sharedSession ?? unauthenticatedSession;
}

export const useAuth = useSession;

export function SessionProvider({ children }: { children: ReactNode }) {
  const session = useSessionState();
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export const AuthProvider = SessionProvider;

/* ------------------------------------------------------------------ */
/* ProtectedRoute                                                      */
/* ------------------------------------------------------------------ */

export function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: ReactNode;
  requireAdmin?: boolean;
}) {
  const { user, isAuthenticated, isPending, isProfileLoaded } = useSession();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);

  if (!ready || isPending || (isAuthenticated && !isProfileLoaded)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#5048E5]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const redirectPath = location.pathname === "/login" ? "/dashboard" : location.pathname;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  if (requireAdmin && !user?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!requireAdmin && user?.isAdmin && location.pathname.startsWith("/dashboard")) {
    return <Navigate to="/admin" replace />;
  }

  // OAuth users without a company name complete their profile first.
  // Only redirect once the profile has actually loaded — otherwise the
  // base (pre-enrichment) session has no companyName and loops.
  if (
    !requireAdmin &&
    isProfileLoaded &&
    user &&
    !user.isAdmin &&
    !user.companyName &&
    location.pathname !== "/complete-profile"
  ) {
    return <Navigate to="/complete-profile" replace />;
  }

  return <MfaGate isAdmin={!!user?.isAdmin}>{children}</MfaGate>;
}
